import { InfoCircleOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Loading } from '../../components/Loading/Loading';

export interface CapabilityVisualPair {
  beforeObjectId: string;
  afterObjectId: string;
  objectType: string;
  beforeTitle?: string;
  afterTitle?: string;
  match: 'same_object' | 'same_title' | 'same_properties';
  beforeProperties: Record<string, any>;
  afterProperties: Record<string, any>;
  propertyChanges: Array<{ path: string; before: unknown; after: unknown }>;
}

export interface VisualAnnotation {
  index: number;
  type: 'structure' | 'style' | 'label' | 'property';
  label: string;
  path: string;
  before: unknown;
  after: unknown;
}

const tenant = 'psdemo.eu.qlikcloud.com';
const webIntegrationId = 'Ffew3J6gRaHJkyB0QIRLKglgP8Ieloa0';
let qlikPromise: Promise<any> | undefined;

function loadCapabilityApi(): Promise<any> {
  if (qlikPromise) return qlikPromise;
  qlikPromise = new Promise((resolve, reject) => {
    const connect = () => {
      const requireJs: any = (window as any).require;
      requireJs.config({ baseUrl: `https://${tenant}/resources`, webIntegrationId });
      requireJs(['js/qlik'], resolve, reject);
    };
    if ((window as any).require) return connect();
    const script = document.createElement('script');
    script.src = `https://${tenant}/resources/assets/external/requirejs/require.js`;
    script.onload = connect;
    script.onerror = () => reject(new Error('Could not load Qlik Capability API'));
    document.head.appendChild(script);
  });
  return qlikPromise;
}

export const CapabilityVisualDiff: React.FC<{ appId: string; beforeLabel?: string; afterLabel: string; afterIsProperties?: boolean; deferRender?: boolean; pair: CapabilityVisualPair; annotations?: VisualAnnotation[]; showAnnotations?: boolean }> = ({ appId, beforeLabel = 'Current Git version', afterLabel, afterIsProperties = false, deferRender = false, pair, annotations = [], showAnnotations = true }) => {
  const suffix = useMemo(() => pair.afterObjectId.replace(/[^a-z0-9]/gi, '').slice(0, 18), [pair.afterObjectId]);
  const beforeElementId = `clickci-before-${suffix}`;
  const afterElementId = `clickci-after-${suffix}`;
  const [renderRequested, setRenderRequested] = useState(!deferRender);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>(deferRender ? 'idle' : 'loading');
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!renderRequested) return;
    let cancelled = false;
    let syntheticVisualization: any;
    const render = async () => {
      try {
        setState('loading'); setError(undefined);
        const qlik = await loadCapabilityApi();
        if (cancelled) return;
        const app = qlik.openApp(appId, {
          host: tenant,
          prefix: '/',
          port: 443,
          isSecure: true,
          webIntegrationId,
        });
        if (afterIsProperties) {
          await app.getObject(beforeElementId, pair.beforeObjectId);
          const afterProperties = JSON.parse(JSON.stringify(pair.afterProperties));
          afterProperties.qInfo = { ...(afterProperties.qInfo ?? {}), qId: `clickci-after-${Date.now()}` };
          const afterType = pair.afterProperties?.visualization ?? pair.afterProperties?.qInfo?.qType ?? pair.objectType;
          syntheticVisualization = await app.visualization.create(afterType, null, afterProperties);
          await syntheticVisualization.show(afterElementId);
        } else {
          const beforeProperties = JSON.parse(JSON.stringify(pair.beforeProperties));
          beforeProperties.qInfo = { ...(beforeProperties.qInfo ?? {}), qId: `clickci-before-${Date.now()}` };
          const beforeType = pair.beforeProperties?.visualization ?? pair.beforeProperties?.qInfo?.qType ?? pair.objectType;
          syntheticVisualization = await app.visualization.create(beforeType, null, beforeProperties);
          await syntheticVisualization.show(beforeElementId);
          await app.getObject(afterElementId, pair.afterObjectId);
        }
        if (!cancelled) setState('ready');
      } catch (caught) {
        if (!cancelled) {
          setState('error');
          setError(caught instanceof Error ? caught.message : 'Capability API rendering failed');
        }
      }
    };
    void render();
    return () => {
      cancelled = true;
      if (syntheticVisualization?.close) syntheticVisualization.close();
    };
  }, [afterElementId, afterIsProperties, appId, beforeElementId, pair, renderRequested]);

  const structural = annotations.find(annotation => annotation.type === 'structure');
  const label = annotations.find(annotation => annotation.type === 'label');
  const styles = annotations.filter(annotation => annotation.type === 'style');

  const renderPane = (side: 'before' | 'after') => {
    const title = side === 'before' ? pair.beforeTitle ?? pair.beforeObjectId : pair.afterTitle ?? pair.afterObjectId;
    const loadingTitle = side === 'before' ? 'Opening Git version in Qlik' : 'Opening current Qlik App';
    const valueFor = (annotation: VisualAnnotation) => side === 'before' ? annotation.before : annotation.after;
    return <div className={`capability-pane ${showAnnotations && structural ? 'has-structure-annotation' : ''}`}>
      {showAnnotations && structural && <div className="structure-annotation"><span>{structural.index}</span>{structural.label}</div>}
      <header><span>{side === 'before' ? `Before · ${beforeLabel}` : `After · ${afterLabel}`}</span><strong className={showAnnotations && label ? 'has-label-annotation' : ''}>{showAnnotations && label && <i>{label.index}</i>}{title}</strong></header>
      <div className={`capability-chart-wrap ${state === 'loading' ? 'is-loading' : ''}`}>
        <div id={side === 'before' ? beforeElementId : afterElementId} className="capability-chart" />
        {state === 'loading' && (
          <div className="capability-qlik-loading" aria-live="polite">
            <strong className="capability-loading-title">{loadingTitle}</strong>
            <Loading message={loadingTitle} />
            <small>Connecting to Qlik Cloud and rendering the object with Capability API.</small>
          </div>
        )}
        {showAnnotations && styles.length > 0 && <div className="style-annotations">{styles.map(annotation => <span key={`${side}:${annotation.path}`}><i>{annotation.index}</i><b style={colorStyle(valueFor(annotation))} />{annotation.label}</span>)}</div>}
      </div>
    </div>;
  };

  return (
    <section className="capability-diff-card">
      {renderPane('before')}
      {renderPane('after')}
      <div className={`capability-render-state is-${state}`}>
        {state === 'idle' && <><InfoCircleOutlined /> Property evidence is ready. <button type="button" onClick={() => setRenderRequested(true)}>Render live chart diff</button> after opening Qlik once in this browser.</>}
        {state === 'loading' && <><LoadingOutlined spin /> Rendering both versions with Qlik Capability API…</>}
        {state === 'ready' && <><InfoCircleOutlined /> Live Qlik rendering. {afterIsProperties ? 'The current App is shown on the left; incoming Git properties are rendered on the right.' : 'Git properties are shown on the left; the actual Qlik object is shown on the right.'}</>}
        {state === 'error' && <><WarningOutlined /> {error}. Open the Preview in Qlik once to establish the tenant session, then refresh this review.</>}
      </div>
    </section>
  );
};

const colorStyle = (value: unknown): React.CSSProperties => {
  if (typeof value !== 'string') return {};
  const normalized = value.trim();
  return /^(#[0-9a-f]{3,8}|rgb\(|hsl\(|[a-z]+$)/i.test(normalized) ? { backgroundColor: normalized } : {};
};
